import { useEffect, useRef } from "react";
import NotificationService from "../services/NotificationService";
import { fetchTodayBirthdays } from "../services/BirthdayService";

/**
 * Hook personnalisé pour vérifier et afficher une notification
 * lorsqu'il y a des anniversaires aujourd'hui
 *
 * @param {boolean} enabled - Active ou désactive la vérification (défaut: true)
 * @param {number} checkIntervalMs - Intervalle de vérification en ms (défaut: 1 heure)
 */
export function useBirthdayNotification(enabled = true, checkIntervalMs = 3600000) {
  const lastNotificationDateRef = useRef(null);
  const hasShownTodayRef = useRef(false);

  const checkAndNotify = async () => {
    if (!enabled) return;

    try {
      const todayBirthdays = await fetchTodayBirthdays();

      if (todayBirthdays.length === 0) {
        // Réinitialiser le flag si aucun anniversaire aujourd'hui
        const today = new Date().toDateString();
        if (lastNotificationDateRef.current !== today) {
          hasShownTodayRef.current = false;
        }
        return;
      }

      // Vérifier si on a déjà affiché une notification aujourd'hui
      const today = new Date().toDateString();
      if (hasShownTodayRef.current && lastNotificationDateRef.current === today) {
        return; // Déjà notifié aujourd'hui
      }

      // Afficher la notification
      const usernames = todayBirthdays.map((b) => `@${b.username}`).join(", ");
      const message =
        todayBirthdays.length === 1
          ? `🎉 Joyeux anniversaire ${usernames} ! 🎂`
          : `🎉 Joyeux anniversaire à ${usernames} ! 🎂`;

      NotificationService.success(message, {
        autoClose: 8000, // 8 secondes pour les anniversaires
        position: "top-right",
        icon: "🎂",
      });

      // Marquer comme notifié pour aujourd'hui
      hasShownTodayRef.current = true;
      lastNotificationDateRef.current = today;
    } catch (error) {
      // Ne pas afficher d'erreur pour les notifications d'anniversaire
      // pour éviter de spammer l'utilisateur
      console.error("Erreur lors de la vérification des anniversaires:", error);
    }
  };

  useEffect(() => {
    if (!enabled) return;

    // Vérifier immédiatement au chargement
    checkAndNotify();

    // Vérifier périodiquement
    const intervalId = setInterval(() => {
      checkAndNotify();
    }, checkIntervalMs);

    // Vérifier aussi au changement de jour (toutes les minutes pour détecter le changement)
    const dayCheckInterval = setInterval(() => {
      const now = new Date();
      const currentHour = now.getHours();
      const currentMinute = now.getMinutes();

      // Réinitialiser le flag à minuit (00:00)
      if (currentHour === 0 && currentMinute === 0) {
        hasShownTodayRef.current = false;
        lastNotificationDateRef.current = null;
        // Vérifier immédiatement après minuit
        setTimeout(() => {
          checkAndNotify();
        }, 1000);
      }
    }, 60000); // Vérifier toutes les minutes

    return () => {
      clearInterval(intervalId);
      clearInterval(dayCheckInterval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, checkIntervalMs]);
}
